// src/pages/ResetPasswordPage.tsx
import React, { Suspense, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';

const ResetPasswordPageContent: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            showNotification(t('resetPassword.noTokenError'), 'error');
            navigate('/forgot-password');
        }
    }, [token, navigate, showNotification, t]);

    const validationSchema = Yup.object({
        password: Yup.string().min(6, t('validation.passwordLength')).required(t('validation.required')),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password')], t('validation.passwordsMustMatch'))
            .required(t('validation.required')),
    });

    return (
        <div className="flex items-center justify-center py-12">
            <div className="max-w-md w-full space-y-8 bg-gray-800 p-10 rounded-xl shadow-lg">
                <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
                    {t('resetPassword.title')}
                </h2>
                <Formik
                    initialValues={{ password: '', confirmPassword: '' }}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        try {
                            await api.post('/Auth/reset-password', { token, password: values.password });
                            showNotification(t('resetPassword.successMessage'), 'success');
                            navigate('/login');
                        } catch (error: any) {
                            const message = error.response?.data?.message || t('resetPassword.errorMessage');
                            showNotification(message, 'error');
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form className="mt-8 space-y-6">
                            <div>
                                <label htmlFor="password" className="sr-only">{t('common.password')}</label>
                                <Field
                                    type="password" name="password" required
                                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder={t('resetPassword.newPasswordPlaceholder')}
                                />
                                <ErrorMessage name="password" component="p" className="text-red-400 text-xs italic mt-1" />
                            </div>
                            <div>
                                <label htmlFor="confirmPassword" className="sr-only">{t('resetPassword.confirmPasswordLabel')}</label>
                                <Field
                                    type="password" name="confirmPassword" required
                                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                                />
                                <ErrorMessage name="confirmPassword" component="p" className="text-red-400 text-xs italic mt-1" />
                            </div>
                            <div>
                                <button type="submit" disabled={isSubmitting} className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                                    {isSubmitting ? t('common.loading') : t('resetPassword.submitButton')}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

const ResetPasswordPage = () => (
    <Suspense fallback={<div className="text-center p-8 text-white">Ładowanie...</div>}>
        <ResetPasswordPageContent />
    </Suspense>
);

export default ResetPasswordPage;
